import { useState } from "react";
import { Users, Sparkles, Crown } from "lucide-react";
import { CommunityChat } from "./CommunityChat";
import { PrivateAIOracle } from "./PrivateAIOracle";
import { GlobalSoccerOracle } from "./GlobalSoccerOracle";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";
import { TermInfoLabel } from "@/components/help/TermInfoLabel";
import type { ChatMessage } from "@/types";

interface Props {
  messages: ChatMessage[];
  onSend: (text: string) => void;
  onAiReply?: (content: string) => void;
  matchContext?: string;
  matchId?: string;
}

type TabKey = "community" | "match" | "global";

export function CommunicationTabs(props: Props) {
  const { t } = useLanguage();
  const [tab, setTab] = useState<TabKey>("global");
  const switchTab = (next: TabKey) => {
    if (next !== tab) void trackEvent("ai_channel_switched", { from: tab, to: next });
    setTab(next);
  };
  return (
    <div className="flex h-full flex-col gap-2">
      <div className="grid grid-cols-3 gap-1 rounded-xl bg-accent/30 p-1">
        <button
          onClick={() => switchTab("global")}
          className={`relative flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-bold transition ${
            tab === "global"
              ? "bg-gradient-to-r from-[oklch(0.45_0.18_280)] to-[oklch(0.6_0.22_320)] text-white shadow-md ring-1 ring-[oklch(0.78_0.16_85)]/60"
              : "text-muted-foreground ring-1 ring-[oklch(0.78_0.16_85)]/40 hover:ring-[oklch(0.78_0.16_85)]/70"
          }`}
        >
          <Crown className={`h-3.5 w-3.5 ${tab === "global" ? "text-[oklch(0.92_0.14_85)]" : "text-[oklch(0.78_0.16_85)]"}`} />
          <span>{t("comm_tab_global_oracle")}</span>
          <span
            className={`hidden sm:inline rounded-full px-1.5 py-[1px] text-[8px] font-black tracking-wider ${
              tab === "global"
                ? "bg-white/25 text-white"
                : "bg-[oklch(0.78_0.16_85)]/15 text-[oklch(0.55_0.16_75)]"
            }`}
          >
            PREMIUM
          </span>
          <TermInfoLabel term="global_oracle" showLabel={false} iconClassName={tab === "global" ? "text-white/80 hover:text-white" : ""} />
        </button>
        <button
          onClick={() => switchTab("community")}
          className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-bold transition ${tab === "community" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
        >
          <Users className="h-3.5 w-3.5" /> {t("comm_tab_community")}
        </button>
        <button
          onClick={() => switchTab("match")}
          className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-bold transition ${tab === "match" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
        >
          <Sparkles className="h-3.5 w-3.5" />
          <span>{t("comm_tab_match_ai")}</span>
          <TermInfoLabel term="match_ai" showLabel={false} />
        </button>
      </div>
      <div className="flex-1 min-h-0">
        <div className={`h-full ${tab === "community" ? "block" : "hidden"}`}>
          <CommunityChat matchId={props.matchId} />
        </div>
        <div className={`h-full ${tab === "match" ? "block" : "hidden"}`}>
          <PrivateAIOracle
            messages={props.messages}
            onSend={props.onSend}
            onAiReply={props.onAiReply}
            matchContext={props.matchContext}
          />
        </div>
        <div className={`h-full ${tab === "global" ? "block" : "hidden"}`}>
          <GlobalSoccerOracle />
        </div>
      </div>
    </div>
  );
}